import { SerializedStyles } from "@emotion/react";
import { Ref, memo, useCallback, useMemo, useState } from "react";
import { styles } from "src/apps/AppFlow/pages/styles";
import { DoughnutChart, LabeledSlider, Legend, Skeleton, StickyButtonContainer } from "src/components";
import { BoldText, Button, HeaderText, RegularText, SemiBoldText } from "src/components/v1";
import { useWindowSize } from "src/hooks";
import { createStyles } from "src/styles";
import getDeviceType from "src/utilities/interface/getDeviceType";
import { BlockFocus } from "src/utilities/react";

import {
  constants,
  calculateCompoundInterest,
  calculateSimpleInterest,
  colors,
  enums,
  formatters,
} from "@fraction/shared";

export interface EstimateProps {
  onChangeAnnualAppreciation?: (val: number) => void;
  amountRequested: number;
  propertyValue: number;

  // Appreciation rate info. Rate is expected as an integer where (1 = 0.01%)
  annualAppreciation?: number;

  // Interest rate info. Rates are expected as floats where (1 = 100%)
  minimumRate?: number;
  maximumRate?: number;
  interestType?: enums.InterestType;
  termLength?: number;

  focusRef?: Ref<HTMLDivElement>;
  onNext?: () => void;
  onBack?: () => void;
  style?: SerializedStyles;

  // injected by FlowLayout
  scrollElementId?: string;
  blockFocus?: BlockFocus;
}

const PAGE_STYLES = createStyles({
  pSkeleton: {
    marginBottom: 6,
  },
});

const Estimate = ({
  onChangeAnnualAppreciation,
  amountRequested,
  propertyValue,
  annualAppreciation,
  minimumRate = 0,
  maximumRate = 0,
  interestType = enums.InterestType.SIMPLE,
  termLength = 5,
  onNext,
  onBack,
  style,
  focusRef,
  scrollElementId,
}: EstimateProps) => {
  const minRate = minimumRate * 100;
  const maxRate = maximumRate * 100;

  const ratesLoaded = minimumRate && maximumRate;

  const [fallbackAppreciation, setFallbackAppreciation] = useState(minRate);
  const appreciation = annualAppreciation ?? fallbackAppreciation;
  const handler = onChangeAnnualAppreciation ?? setFallbackAppreciation;

  const marketSlumpRate = minRate;
  const extremeGrowthRate = maxRate;

  let visualizationHeader = "Standard growth";
  let visualizationColor = colors.palette.GREEN;
  if (appreciation < marketSlumpRate) {
    visualizationHeader = "Market slump";
    visualizationColor = colors.palette.RED;
  }
  if (appreciation > extremeGrowthRate) {
    visualizationHeader = "Extreme growth";
    visualizationColor = colors.palette.YELLOW;
  }
  const effectiveInterest = Math.min(Math.max(appreciation, minRate), maxRate);

  const calculateInterest = useMemo(
    () =>
      interestType === enums.InterestType.COMPOUND ? calculateCompoundInterest : calculateSimpleInterest,
    [interestType]
  );

  const futureHomeValue = calculateInterest(propertyValue, appreciation / 100, termLength);
  const futureLoanValue = calculateInterest(amountRequested || 0, effectiveInterest / 100, termLength);
  const futureEquityValue = futureHomeValue - futureLoanValue;

  const sliderMax = Math.floor(Math.max(maxRate + 2, 20));
  const { width: windowWidth } = useWindowSize();
  const { phone } = getDeviceType(windowWidth);

  const valueFormatter = useCallback(
    (val: number) =>
      formatters.number.getCurrencyWords(val, {
        translations: [
          {
            divisor: 1000000,
            suffix: "M",
            remainder: 1,
          },
          {
            divisor: 1000,
            suffix: "k",
            remainder: 10, // only do for 10k and greater
            digits: 0,
          },
        ],
      }),
    []
  );

  const sliderFormatter = useCallback((val: number) => `${val.toFixed(1)}%`, []);

  const explainer = ratesLoaded ? (
    <div css={styles.visualizationExplainerContainer}>
      <SemiBoldText style={[styles.visualizationHeader, { color: visualizationColor }]}>
        {visualizationHeader}
      </SemiBoldText>
      <RegularText paragraph style={styles.visualizationParagraph}>
        With a yearly appreciation of <BoldText paragraph>{`${appreciation.toFixed(2)}%`}</BoldText>, in{" "}
        {constants.DEFAULT_TERM_LENGTH}
        years time, your home will be worth{" "}
        <BoldText paragraph>{formatters.number.getCurrencyFromNumber(futureHomeValue)}</BoldText>. With an
        interest rate of <BoldText paragraph>{`${effectiveInterest.toFixed(2)}%`}</BoldText>, the interest
        cost will be{" "}
        <BoldText paragraph>
          {formatters.number.getCurrencyFromNumber(futureLoanValue - amountRequested)}
        </BoldText>{" "}
        over the {constants.DEFAULT_TERM_LENGTH} years.
      </RegularText>
    </div>
  ) : (
    <div css={styles.visualizationExplainerContainer}>
      <Skeleton width="200px" height="25px" style={styles.visualizationHeader} />
      <Skeleton width="320px" height="20px" style={PAGE_STYLES.pSkeleton} />
      <Skeleton width="320px" height="20px" style={PAGE_STYLES.pSkeleton} />
      <Skeleton width="320px" height="20px" style={PAGE_STYLES.pSkeleton} />
      <Skeleton width="320px" height="20px" style={PAGE_STYLES.pSkeleton} />
      <Skeleton width="40%" height="20px" />
    </div>
  );

  return (
    <div ref={focusRef} css={[styles.page, style]}>
      <div id="estimate" css={[styles.contentContainer, styles.centeredContentContainer]}>
        <div css={styles.centeredInnerHeaderContainer}>
          <HeaderText style={[styles.centeredText, styles.centeredHeader, styles.header]}>
            How does it work?
          </HeaderText>
          <RegularText paragraph style={[styles.text, styles.centeredText]}>
            You only ever pay a fair share because your interest rate is tied to the appreciation rate of your
            home.
          </RegularText>
        </div>
        <div css={styles.wrappingVisualizationContainer}>
          <div css={styles.sliderContainer}>
            <SemiBoldText style={styles.sliderLabel}>Yearly appreciation</SemiBoldText>
            <LabeledSlider
              style={styles.input}
              onChange={handler}
              value={appreciation}
              formatter={sliderFormatter}
              min={-1}
              max={sliderMax}
              step={0.1}
              markers={[minRate, maxRate]}
              color={visualizationColor}
            />
            {phone && explainer}
          </div>
          <div css={styles.chartContainer}>
            <div css={styles.visualizationContainer}>
              {ratesLoaded ? (
                <DoughnutChart
                  innerLabel={`Equity\nin ${termLength} years`}
                  values={[futureEquityValue, futureLoanValue]}
                  colors={[colors.palette.YELLOW, colors.palette.GREEN]}
                  valueFormatter={valueFormatter}
                />
              ) : (
                <Skeleton width="240px" height="240px" circle />
              )}
              {!phone && explainer}
            </div>
            <Legend
              style={styles.legend}
              values={[
                {
                  label: "Fraction",
                  color: "#82c882",
                },
                {
                  label: "Personal equity",
                  color: "#F4BE0A",
                },
              ]}
              loading={!ratesLoaded}
            />
          </div>
        </div>
        <StickyButtonContainer scrollElement={`#${scrollElementId}`} boundaryElement="#estimate">
          <div css={styles.buttonContainer}>
            <Button align="left" type="ghost" narrow onClick={onBack}>
              &#8592; Back
            </Button>
            <Button narrow onClick={onNext} disabled={!ratesLoaded}>
              Next
            </Button>
          </div>
        </StickyButtonContainer>
      </div>
    </div>
  );
};

export default memo(Estimate);
